import React, { useState, useEffect, useCallback, useRef, useContext } from 'react';
import { Box, useMediaQuery, useTheme, Button, Typography, IconButton } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useSocket } from '../../context/SocketContext';
import { useAuth } from '../../context/AuthContext';
import { useUnreadMessages } from '../../context/UnreadMessagesContext';
import api from '../../utils/api';
import useThemeStyles from '../../theme/useThemeStyles';
import ConversationSidebar from './ConversationSidebar';
import MessageList from './MessageList';
import MessageInput from './MessageInput';
import pushNotifications from '../../utils/pushNotifications';
import CoupleInformationModal from '../ChatInterface/CoupleInformationModal';

// Custom hook to safely use business context (may not be available for couples)
const useBusinessSafe = () => {
  try {
    const BusinessContext = require('../../context/BusinessContext').BusinessContext;
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const context = useContext(BusinessContext);
    return context || { selectedBusiness: null };
  } catch (error) {
    return { selectedBusiness: null };
  }
};

const ChatContainer = ({ 
  initialChatId = null,
  onCreateChat = null,
  chatFilter = 'all', // 'all', 'suppliers', 'ai'
  showSidebar = true,
}) => {
  const _styles = useThemeStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { socket, isConnected } = useSocket();
  const { user } = useAuth();
  const { selectedBusiness } = useBusinessSafe();
  const { refreshUnreadMessagesCount } = useUnreadMessages();
  const { conversationId: urlConversationId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [conversations, setConversations] = useState([]);
  const [activeChatId, setActiveChatId] = useState(initialChatId || urlConversationId || null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [_typingUserId, setTypingUserId] = useState(null);
  const [isFunctionCalling, setIsFunctionCalling] = useState(false);
  const [functionCallName, setFunctionCallName] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showWeddingDetails, setShowWeddingDetails] = useState(false);
  const [creatingContact, setCreatingContact] = useState(false);
  const typingTimeoutRef = useRef(null);
  const isNavigatingBackRef = useRef(false);
  // Mobile: show list first (WhatsApp-style); desktop: always show sidebar
  const [_sidebarOpen, setSidebarOpen] = useState(isMobile ? !urlConversationId : true);
  const [processingSupplierChat, setProcessingSupplierChat] = useState(false);
  const [conversationsLoaded, setConversationsLoaded] = useState(false);
  
  // Determine user type
  const userType = user?.role === 'supplier' ? 'supplier' : 'couple';
  
  // Sync activeChatId with URL - but don't restore chat when user just navigated back
  useEffect(() => {
    if (!urlConversationId) {
      isNavigatingBackRef.current = false;
      setActiveChatId(null);
      return;
    }
    if (isNavigatingBackRef.current) return;
    setActiveChatId(urlConversationId);
  }, [urlConversationId]);

  // Mobile: sync sidebar with URL - show list when no conversation, chat when in conversation
  useEffect(() => {
    if (isMobile) {
      setSidebarOpen(!urlConversationId);
    }
  }, [isMobile, urlConversationId]);
  
  // Fetch conversations
  const fetchConversations = useCallback(async () => {
    try {
      setLoading(true);
      // Pass businessId if available (for suppliers)
      const params = {};
      if (userType === 'supplier' && selectedBusiness?.business?._id) {
        params.businessId = selectedBusiness.business._id;
      }
      const response = await api.get('/api/chat/conversations', { params });
      let filtered = response.data || [];
      
      // For suppliers, exclude AI chats (they should only appear in Lumi Assistant)
      if (userType === 'supplier') {
        filtered = filtered.filter(c => 
          c.type !== 'ai' && 
          c.type !== 'ai-couple' && 
          c.type !== 'ai-supplier'
        );
      }
      
      // Apply filter
      if (chatFilter === 'suppliers') {
        filtered = filtered.filter(c => c.type === 'supplier' || c.type === 'supplier-supplier');
      } else if (chatFilter === 'ai') {
        filtered = filtered.filter(c => c.type === 'ai' || c.type === 'ai-couple' || c.type === 'ai-supplier');
      }
      
      // Deduplicate chats - keep the one with the most recent activity
      const chatMap = new Map();
      filtered.forEach(chat => {
        const key = `${chat.type}-${chat.business?._id || chat.business}-${chat.wedding?._id || chat.wedding}`;
        const existing = chatMap.get(key);
        
        if (!existing) {
          chatMap.set(key, chat);
        } else {
          // Keep the one with more messages or more recent activity
          const existingMessages = existing.messages?.length || 0;
          const currentMessages = chat.messages?.length || 0;
          const existingActivity = new Date(existing.lastActivity || 0).getTime();
          const currentActivity = new Date(chat.lastActivity || 0).getTime();
          
          if (currentMessages > existingMessages || 
              (currentMessages === existingMessages && currentActivity > existingActivity)) {
            chatMap.set(key, chat);
          }
        }
      });
      
      const deduplicated = Array.from(chatMap.values());
      setConversations(deduplicated);
      setConversationsLoaded(true);
      
      // Set initial active chat if provided
      if (initialChatId && !activeChatId) {
        const chatExists = filtered.find(c => c._id === initialChatId);
        if (chatExists) {
          setActiveChatId(initialChatId);
        }
      } else if (urlConversationId && !activeChatId && !isNavigatingBackRef.current) {
        const chatExists = filtered.find(c => c._id === urlConversationId);
        if (chatExists) {
          setActiveChatId(urlConversationId);
        }
      } else if (!activeChatId && filtered.length > 0 && !urlConversationId && !isMobile) {
        // Auto-select first conversation on desktop only (mobile: show list first)
        setActiveChatId(filtered[0]._id);
      }
    } catch (error) {
      setConversationsLoaded(true); // Mark as loaded even on error
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- verified intentional deps
  }, [initialChatId, chatFilter, activeChatId, urlConversationId, userType, isMobile]);
  
  // Fetch messages for active chat
  const fetchMessages = useCallback(async (chatId, silent = false) => {
    if (!chatId) return;
    
    try {
      if (!silent) {
        setLoading(true);
      }
      const response = await api.get(`/api/chat/conversations/${chatId}/messages`);
      const fetchedMessages = response.data || [];
      
      // Only update if messages actually changed to avoid unnecessary re-renders
      // But preserve sender data from existing messages if backend didn't populate it
      setMessages(prev => {
        const prevIds = new Set(prev.map(m => m._id?.toString()));
        const newIds = new Set(fetchedMessages.map(m => m._id?.toString()));
        const idsChanged = prev.length !== fetchedMessages.length || 
                          ![...newIds].every(id => prevIds.has(id));
        
        if (idsChanged) {
          // Merge fetched messages with existing messages to preserve sender data
          const prevMap = new Map(prev.map(m => [m._id?.toString(), m]));
          return fetchedMessages.map(msg => {
            const existing = prevMap.get(msg._id?.toString());
            // If backend message doesn't have populated sender but existing does, preserve it
            if (existing && existing.sender && typeof existing.sender === 'object' && 
                (!msg.sender || typeof msg.sender === 'string' || !msg.sender.profilePicture)) {
              return {
                ...msg,
                sender: existing.sender
              };
            }
            return msg;
          });
        }
        return prev; // No change, avoid re-render
      });
      
      // Mark messages as read
      try {
        await api.post(`/api/chat/conversations/${chatId}/read`);
        // Update conversation unread count locally
        setConversations(prev => 
          prev.map(conv => 
            conv._id === chatId 
              ? { 
                  ...conv, 
                  unreadCounts: {
                    ...conv.unreadCounts,
                    [userType]: 0
                  }
                }
              : conv
          )
        );
        refreshUnreadMessagesCount();
      } catch (error) {
      }
    } catch (error) {
      if (!silent) {
        setMessages([]);
      }
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [userType, refreshUnreadMessagesCount]);
  

  // Send message
  const handleSendMessage = useCallback(async (content) => {
    if (!activeChatId || !content.trim() || sending) return;
    
    const tempId = 'temp-' + Date.now();
    const messageContent = content.trim();
    
    try {
      setSending(true);
      
      // Add temporary message to UI
      const tempMessage = {
        _id: tempId,
        content: messageContent,
        senderType: userType,
        sender: user?._id,
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, tempMessage]);
      
      // Update last message time
      lastMessageTimeRef.current = Date.now();
      
      // Send message
      const response = await api.post(`/api/chat/conversations/${activeChatId}/messages`, {
        content: messageContent,
      });
      
      // Check if function calling is happening
      if (response.data.functionCalling) {
        setIsFunctionCalling(true);
        setFunctionCallName(response.data.functionName || null);
      }
      
      // Wait for socket event to replace temp message (socket should deliver within 1 second)
      // If socket doesn't deliver, we'll replace temp with real message after timeout
      setTimeout(() => {
        setMessages(prev => {
          // Check if real message exists - handle both regular and file messages
          const hasRealMessage = prev.some(m => {
            if (m._id?.toString().startsWith('temp-')) return false;
            if (m.senderType !== userType) return false;
            
            // For file messages, parse JSON to compare
            try {
              const tempParsed = JSON.parse(messageContent);
              const msgParsed = JSON.parse(m.content);
              if (tempParsed.type === 'file' && msgParsed.type === 'file') {
                return tempParsed.fileId === msgParsed.fileId && 
                       tempParsed.fileUrl === msgParsed.fileUrl;
              }
            } catch {
              // Not JSON, compare as strings
            }
            
            return m.content === messageContent;
          });
          
          if (!hasRealMessage) {
            // Socket didn't deliver, fetch just this chat's messages (not all conversations)
            fetchMessages(activeChatId);
          }
          
          // Remove temp message (socket should have replaced it)
          return prev.filter(msg => msg._id !== tempId);
        });
      }, 1000);
      
      // Update conversations list locally without full refresh
      setConversations(prev => 
        prev.map(conv => {
          if (conv._id === activeChatId) {
            return {
              ...conv,
              lastActivity: new Date(),
              messages: [...(conv.messages || []), tempMessage]
            };
          }
          return conv;
        })
      );
      
    } catch (error) {
      // Remove temporary message on error
      setMessages(prev => prev.filter(msg => msg._id !== tempId));
    } finally {
      setSending(false);
      setIsFunctionCalling(false);
      setFunctionCallName(null);
    }
  }, [activeChatId, userType, user, sending, fetchMessages]);
  
  // Fix handleFileSelect dependency
  const handleFileSelectMemo = useCallback((file) => {
    if (!activeChatId || !file) return;
    
    // Create structured message with file metadata
    const fileMessage = {
      type: 'file',
      fileId: file._id,
      fileName: file.name,
      fileUrl: file.fileUrl
    };
    
    // Send as JSON string in content field
    handleSendMessage(JSON.stringify(fileMessage));
  }, [activeChatId, handleSendMessage]);
  
  // Handle chat selection
  const handleSelectChat = useCallback(async (chatId) => {
    // Update URL to reflect selected chat - use supplier route for suppliers, couple route for couples
    const chatPath = userType === 'supplier' ? `/supplier/messages/${chatId}` : `/chat/${chatId}`;
    navigate(chatPath, { replace: true });
    setActiveChatId(chatId);
    await fetchMessages(chatId);
    if (isMobile) {
      setSidebarOpen(false);
    }
  }, [fetchMessages, isMobile, navigate, userType]);
  
  // Initialize push notifications and request permission
  useEffect(() => {
    if (user) {
      // Request permission immediately when user opens chat
      pushNotifications.requestPermission().then(granted => {
        if (granted) {
          pushNotifications.initialize();
        } else {
        }
      });
    }
  }, [user]);

  // Join/leave chat room when activeChatId changes
  useEffect(() => {
    if (!socket || !isConnected || !activeChatId) {
      return;
    }
    
    socket.emit('join-chat', activeChatId);
    
    // Listen for join-chat errors
    const handleJoinError = (_error) => {
      // Silently handle errors
    };
    
    socket.once('error', handleJoinError);
    
    return () => {
      socket.emit('leave-chat', activeChatId);
      socket.off('error', handleJoinError);
    };
  }, [socket, isConnected, activeChatId, user, userType]);

  // Socket event listeners for real-time updates
  useEffect(() => {
    if (!socket || !isConnected) {
      return;
    }
    
    // Listen for new messages
    const handleNewMessage = (data) => {
      if (!data || !data.chatId || !data.message) {
        return;
      }
      
      // Always update conversations list first
      setConversations(prev => {
        return prev.map(conv => {
          if (conv._id === data.chatId) {
            const updatedUnreadCounts = { ...conv.unreadCounts };
            if (data.message.senderType !== userType) {
              updatedUnreadCounts[userType] = (updatedUnreadCounts[userType] || 0) + 1;
            }
            return {
              ...conv,
              unreadCounts: updatedUnreadCounts,
              messages: [...(conv.messages || []), data.message],
              lastActivity: new Date(data.message.timestamp || Date.now())
            };
          }
          return conv;
        });
      });
      
      // Update socket activity timestamp
      lastSocketActivityRef.current = Date.now();
      
      if (data.chatId === activeChatId) {
        // Message for current chat - add to messages
        setMessages(prev => {
          // Get sender ID for comparison (handle both object and ID formats)
          const newSenderId = data.message.sender?._id || data.message.sender;
          const isFromCurrentUser = newSenderId && user?._id && (
            newSenderId.toString() === user._id.toString()
          );
          
          // Avoid duplicates by checking message ID first
          const existsById = prev.find(m => {
            if (m._id && data.message._id) {
              return m._id.toString() === data.message._id.toString();
            }
            return false;
          });
          
          if (existsById) {
            // Real message already exists with this ID, don't add duplicate
            return prev;
          }
          
          // Check for temp message to replace (for messages from current user or matching content)
          const tempMessageToReplace = prev.find(m => {
            // Must be a temp message
            if (!m._id?.toString().startsWith('temp-')) {
              return false;
            }
            
            // Check if sender types match
            if (m.senderType !== data.message.senderType) {
              return false;
            }
            
            // If from current user, match by sender ID
            if (isFromCurrentUser) {
              const prevSenderId = m.sender?._id || m.sender;
              if (prevSenderId && newSenderId) {
                if (prevSenderId.toString() === newSenderId.toString()) {
                  return true; // Same sender, likely the same message
                }
              }
            }
            
            // Match by content and timestamp
            let contentMatches = false;
            try {
              const prevContent = m.content;
              const newContent = data.message.content;
              
              // Try parsing as JSON for file messages
              try {
                const prevParsed = JSON.parse(prevContent);
                const newParsed = JSON.parse(newContent);
                if (prevParsed.type === 'file' && newParsed.type === 'file') {
                  contentMatches = prevParsed.fileId === newParsed.fileId && 
                                  prevParsed.fileUrl === newParsed.fileUrl;
                } else {
                  contentMatches = prevContent === newContent;
                }
              } catch {
                // Not JSON, compare as strings
                contentMatches = prevContent === newContent;
              }
            } catch {
              contentMatches = m.content === data.message.content;
            }
            
            // Check timestamp is within 10 seconds (more lenient for network delays)
            const timeDiff = Math.abs(new Date(m.timestamp) - new Date(data.message.timestamp || Date.now()));
            return contentMatches && timeDiff < 10000;
          });
          
          if (tempMessageToReplace) {
            // Replace temp message with real message
            return prev.map(m => 
              m._id === tempMessageToReplace._id ? {
                ...data.message,
                // Ensure sender is properly populated (should come from backend)
                sender: data.message.sender || m.sender,
                timestamp: data.message.timestamp || new Date()
              } : m
            );
          }
          
          // Add new message, ensuring proper structure with sender data
          const newMessage = {
            ...data.message,
            // Ensure sender is properly populated
            sender: data.message.sender || (data.message.senderType !== 'ai' ? { _id: data.message.sender } : null),
            timestamp: data.message.timestamp || new Date()
          };
          
          // Update last message time
          lastMessageTimeRef.current = Date.now();
          
          return [...prev, newMessage];
        });
        
        // Mark as read (don't refresh conversations to avoid flash)
        if (data.message.senderType !== userType) {
          api.post(`/api/chat/conversations/${activeChatId}/read`).catch(() => {});
          // Update conversation unread count locally without full refresh
          setConversations(prev => 
            prev.map(conv => 
              conv._id === activeChatId 
                ? { 
                    ...conv, 
                    unreadCounts: {
                      ...conv.unreadCounts,
                      [userType]: 0
                    }
                  }
                : conv
            )
          );
          refreshUnreadMessagesCount();
        }
      } else {
        // Message for a different chat - show notification
        const chatTitle = data.chat?.business?.name || 
                         data.chat?.wedding?.name || 
                         data.chat?.title || 
                         'New message';
        
        // Show notification if message is from someone else
        // Only show if not currently viewing that chat or tab is not focused
        const isViewingChat = activeChatId === data.chatId;
        const isTabFocused = document.hasFocus() && document.visibilityState === 'visible';
        
        if (data.message.senderType !== userType && (!isViewingChat || !isTabFocused)) {
          pushNotifications.showChatNotification(
            chatTitle,
            data.message.content,
            data.chatId
          );
        }
      }
    };
    
    // Listen for typing indicators
    const handleTyping = (data) => {
      // Normalize chat IDs for comparison (handle string vs ObjectId)
      const normalizedChatId = activeChatId?.toString();
      const normalizedDataChatId = data.chatId?.toString();
      
      if (normalizedDataChatId === normalizedChatId && data.userId !== user?._id) {
        setIsTyping(true);
        setTypingUserId(data.userId);
        // Clear existing timeout
        if (typingTimeoutRef.current) {
          clearTimeout(typingTimeoutRef.current);
        }
        // Set timeout to hide typing indicator after 3 seconds
        typingTimeoutRef.current = setTimeout(() => {
          setIsTyping(false);
          setTypingUserId(null);
        }, 3000);
      }
    };
    
    // Listen for typing stop events
    const handleTypingStop = (data) => {
      // Normalize chat IDs for comparison
      const normalizedChatId = activeChatId?.toString();
      const normalizedDataChatId = data.chatId?.toString();
      
      if (normalizedDataChatId === normalizedChatId && data.userId !== user?._id) {
        setIsTyping(false);
        setTypingUserId(null);
        if (typingTimeoutRef.current) {
          clearTimeout(typingTimeoutRef.current);
        }
      }
    };
    
    // Listen for function call completion
    const handleFunctionCallComplete = (data) => {
      if (data.chatId === activeChatId) {
        setIsFunctionCalling(false);
        setFunctionCallName(null);
        // Refresh messages to show function result
        fetchMessages(activeChatId);
      }
    };
    
    // Add event listeners
    socket.on('new-chat-message', handleNewMessage);
    socket.on('chat-typing', handleTyping);
    socket.on('chat-typing-stop', handleTypingStop);
    socket.on('function-call-complete', handleFunctionCallComplete);
    
    return () => {
      socket.off('new-chat-message', handleNewMessage);
      socket.off('chat-typing', handleTyping);
      socket.off('chat-typing-stop', handleTypingStop);
      // Cleanup typing timeout
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      socket.off('function-call-complete', handleFunctionCallComplete);
    };
  }, [socket, isConnected, activeChatId, user, userType, fetchConversations, fetchMessages, refreshUnreadMessagesCount]);
  
  // Handle supplier query parameter - create or find supplier chat
  useEffect(() => {
    const supplierId = searchParams.get('supplier');
    // Process supplier chat once conversations have finished loading (even if empty)
    // Use conversationsLoaded flag to ensure we've at least attempted to load conversations
    if (supplierId && userType === 'couple' && !processingSupplierChat && !loading && conversationsLoaded) {
      setProcessingSupplierChat(true);
      
      const handleSupplierChat = async () => {
        try {
          // First, try to find existing supplier chat in loaded conversations
          const existingChat = conversations.find(
            chat => chat.type === 'supplier' && 
            (chat.business?._id === supplierId || chat.business === supplierId || 
             chat.business?._id?.toString() === supplierId || chat.business?.toString() === supplierId)
          );
          
          if (existingChat) {
            // Navigate to existing chat
            navigate(`/chat/${existingChat._id}`, { replace: true });
            setActiveChatId(existingChat._id);
          } else {
            // Create new supplier chat - backend will check for duplicates
            const response = await api.post('/api/chat/conversations', {
              type: 'supplier',
              businessId: supplierId,
            });
            
            // Refresh conversations to include the new/returned chat
            // Note: fetchConversations will use the current selectedBusiness automatically
            await fetchConversations();
            
            // Navigate to chat
            navigate(`/chat/${response.data._id}`, { replace: true });
            setActiveChatId(response.data._id);
          }
        } catch (error) {
          // Show user-friendly error message
          if (error.response?.status === 404 && error.response?.data?.message?.includes('Wedding not found')) {}
        } finally {
          setProcessingSupplierChat(false);
          // Remove supplier parameter from URL
          searchParams.delete('supplier');
          setSearchParams(searchParams, { replace: true });
        }
      };
      
      handleSupplierChat();
    }
  }, [searchParams, userType, processingSupplierChat, loading, conversations, conversationsLoaded, navigate, setSearchParams, fetchConversations, selectedBusiness]);

  // Initial load and refetch when selectedBusiness changes
  useEffect(() => {
    if (!processingSupplierChat) {
      fetchConversations();
    }
  }, [fetchConversations, processingSupplierChat, selectedBusiness]);
  
  // Load messages when active chat changes
  useEffect(() => {
    if (activeChatId) {
      fetchMessages(activeChatId);
    } else {
      setMessages([]);
    }
  }, [activeChatId, fetchMessages]);

  // Poll for new messages as fallback (only if socket might not be working)
  const lastMessageTimeRef = useRef(Date.now());
  const lastSocketActivityRef = useRef(Date.now());
  
  useEffect(() => {
    if (!activeChatId) return;
    
    // Update socket activity timestamp when messages arrive via socket
    const checkSocketActivity = setInterval(() => {
      const timeSinceLastSocketActivity = Date.now() - lastSocketActivityRef.current;
      const timeSinceLastMessage = Date.now() - lastMessageTimeRef.current;
      
      // Only poll if:
      // 1. No socket activity in last 10 seconds AND
      // 2. No new messages in last 10 seconds
      // This prevents unnecessary polling when socket is working
      if (timeSinceLastSocketActivity > 10000 && timeSinceLastMessage > 10000) {
        fetchMessages(activeChatId, true); // Silent fetch to avoid flash
        fetchConversations();
      }
    }, 5000); // Check every 5 seconds, but only poll if needed
    
    return () => clearInterval(checkSocketActivity);
  }, [activeChatId, fetchMessages, fetchConversations]);
  
  // Get chat display title
  const getChatDisplayTitle = (chat) => {
    if (!chat) return 'Chat';
    
    // For supplier chats, prioritize business/wedding name over stored title
    if (chat.type === 'supplier') {
      // If user is a supplier, show the wedding name
      if (userType === 'supplier') {
        return chat.wedding?.name || chat.title || 'Wedding Chat';
      }
      // If user is a couple, show the business name
      else {
        return chat.business?.name || chat.title || 'Supplier Chat';
      }
    }
    
    // For supplier-supplier chats
    if (chat.type === 'supplier-supplier') {
      // Show the other business name (not the current user's business)
      if (chat.secondBusiness?.name) {
        return chat.secondBusiness.name;
      }
      if (chat.business?.name) {
        return chat.business.name;
      }
      return chat.title || 'Supplier Chat';
    }
    
    // For AI chats
    if (chat.type === 'ai' || chat.type === 'ai-couple') {
      return 'Chat with Lumi';
    }
    if (chat.type === 'ai-supplier') {
      return 'Lumi Business Assistant';
    }
    
    // Fallback to stored title or default
    return chat.title || 'Chat';
  };
  
  // Get active chat details
  const activeChat = conversations.find(c => c._id === activeChatId);
  
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        height: { xs: 'calc(100vh - 180px)', sm: 'calc(100vh - 160px)', md: 'calc(100vh - 120px)' },
        minHeight: { xs: '400px', sm: '500px', md: '600px' },
        maxHeight: { xs: 'calc(100vh - 180px)', sm: 'calc(100vh - 160px)', md: 'calc(100vh - 120px)' },
        borderRadius: { xs: '8px', sm: '12px', md: '16px' },
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      {/* Mobile: List page (layer 1) - full-width conversations list with white bg */}
      {showSidebar && (
        <Box
          sx={{
            display: { xs: 'block', md: 'none' },
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: '100%',
            height: '100%',
            zIndex: 1,
            backgroundColor: '#fff',
            overflow: 'auto',
            visibility: { xs: activeChatId ? 'hidden' : 'visible', md: 'hidden' },
          }}
        >
          <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', p: 2 }}>
            <ConversationSidebar
              conversations={conversations}
              activeChatId={activeChatId}
              onSelectChat={handleSelectChat}
              onCreateChat={onCreateChat}
              userType={userType}
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
              currentUser={user}
              fullWidth
            />
          </Box>
        </Box>
      )}

      {/* Desktop: Sidebar */}
      {showSidebar && (
        <Box
          sx={{
            display: { xs: 'none', md: 'block' },
            width: '320px',
            flexShrink: 0,
            height: '100%',
          }}
        >
          <ConversationSidebar
            conversations={conversations}
            activeChatId={activeChatId}
            onSelectChat={handleSelectChat}
            onCreateChat={onCreateChat}
            userType={userType}
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            currentUser={user}
          />
        </Box>
      )}
      
      {/* Mobile: Chat page (layer 2) - overlays list when a chat is selected */}
      {/* Desktop: Main chat area - side by side with sidebar */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: '#fff',
          borderRadius: '16px',
          overflow: 'hidden',
          ml: showSidebar ? { xs: 0, md: 2 } : 0,
          height: '100%',
          minHeight: 0,
          // Mobile: overlay on top of list when chat selected
          position: { xs: 'absolute', md: 'relative' },
          top: { xs: 0, md: 'auto' },
          left: { xs: 0, md: 'auto' },
          right: { xs: 0, md: 'auto' },
          bottom: { xs: 0, md: 'auto' },
          width: { xs: '100%', md: 'auto' },
          zIndex: { xs: 2, md: 'auto' },
          // Mobile: only show when chat selected
          visibility: { xs: activeChatId ? 'visible' : 'hidden', md: 'visible' },
          pointerEvents: { xs: activeChatId ? 'auto' : 'none', md: 'auto' },
        }}
      >
        {/* Chat header */}
        {activeChat && (
          <Box
            sx={{
              p: 2,
              borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
              backgroundColor: '#fff',
              flexShrink: 0,
            }}
          >
            <Box sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              justifyContent: 'space-between',
              gap: { xs: 1, sm: 0 }
            }}>
              {/* Left: Back button (mobile) - first so it's on the left */}
              {isMobile && showSidebar && (
                <IconButton
                  onClick={() => {
                    isNavigatingBackRef.current = true;
                    setActiveChatId(null);
                    const basePath = userType === 'supplier' ? '/supplier/messages' : '/chat';
                    // Defer navigate to next tick so setState commits before URL change - prevents chat re-opening on first back
                    setTimeout(() => navigate(basePath, { replace: true }), 0);
                  }}
                  aria-label="Back to chats"
                  size="small"
                  sx={{ mr: 1, flexShrink: 0 }}
                >
                  <ArrowBack fontSize="small" />
                </IconButton>
              )}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ 
                  fontWeight: 600, 
                  fontSize: { xs: '1rem', sm: '1.05rem', md: '1.1rem' }, 
                  color: '#2C3E50',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>
                  {getChatDisplayTitle(activeChat)}
                </Box>
                {activeChat.type === 'ai' || activeChat.type === 'ai-couple' || activeChat.type === 'ai-supplier' ? (
                  <Box sx={{ fontSize: { xs: '0.75rem', sm: '0.8125rem', md: '0.875rem' }, color: '#9e9e9e', mt: 0.5 }}>
                    AI Assistant
                  </Box>
                ) : null}
              </Box>
              
              {/* Action buttons */}
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: { xs: 0.5, sm: 1 },
                ml: { xs: 0, sm: 2 },
                flexWrap: 'wrap',
                width: { xs: 'auto', sm: 'auto' },
                justifyContent: 'flex-end',
                flexShrink: 0
              }}>
                {/* For couples viewing supplier chats - View Profile button */}
                {userType === 'couple' && activeChat.type === 'supplier' && activeChat.business?._id && (
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => navigate(`/dashboard/supplier/${activeChat.business._id}`)}
                      sx={{
                        borderRadius: '8px',
                        textTransform: 'none',
                        fontWeight: 600,
                        px: { xs: 1.5, sm: 2 },
                        py: 0.5,
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        whiteSpace: 'nowrap'
                      }}
                    >
                      View Profile
                    </Button>
                )}
                
                {/* For suppliers viewing couple chats */}
                {userType === 'supplier' && activeChat.type === 'supplier' && activeChat.wedding?._id && (
                  <>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={async () => {
                        try {
                          setCreatingContact(true);
                          // Check if lead already exists
                          const response = await api.get(`/api/leads?wedding=${activeChat.wedding._id}`);
                          const existingLeads = response.data.leads || [];
                          
                          if (existingLeads.length > 0) {
                            // Lead already exists, navigate to CRM
                            navigate('/supplier/crm');
                            return;
                          }
                          
                          // Create lead from chat
                          await api.post(`/api/leads/from-chat/${activeChat._id}`);
                          
                          // Navigate to CRM to see the new lead
                          navigate('/supplier/crm');
                        } catch (err) {
                          // Still navigate to CRM even if there's an error
                          navigate('/supplier/crm');
                        } finally {
                          setCreatingContact(false);
                        }
                      }}
                      disabled={creatingContact}
                      sx={{
                        borderRadius: '8px',
                        textTransform: 'none',
                        fontWeight: 600,
                        px: { xs: 1.5, sm: 2 },
                        py: 0.5,
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {creatingContact ? 'Adding...' : 'Add to CRM'}
                    </Button>
                    
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => setShowWeddingDetails(true)}
                      sx={{
                        borderRadius: '8px',
                        textTransform: 'none',
                        fontWeight: 600,
                        px: { xs: 1.5, sm: 2 },
                        py: 0.5,
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        whiteSpace: 'nowrap'
                      }}
                    >
                      View Wedding Details
                    </Button>
                  </>
                )}
              </Box>
            </Box>
          </Box>
        )}
        
        {/* Message list - scrollable container */}
        <Box
          sx={{
            flex: 1,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
          }}
        >
          <MessageList
            messages={messages}
            currentUserId={user?._id}
            userType={userType}
            chatId={activeChatId}
            isLoading={loading && !messages.length}
            isTyping={isTyping}
            chatContext={activeChat ? {
              business: activeChat.business,
              wedding: activeChat.wedding,
              user: user ? { profilePicture: user.profilePicture } : null
            } : null}
            currentUserName={user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.name || '' : ''}
          />
        </Box>
        
        {/* Message input */}
        {activeChatId && (
          <Box sx={{ flexShrink: 0, position: 'relative' }}>
            {/* Typing indicator bubble for suppliers */}
            {userType === 'supplier' && isTyping && (
              <Box
                sx={{
                  position: 'absolute',
                  bottom: '100%',
                  left: 16,
                  mb: 1,
                  borderRadius: '12px',
                  padding: '8px 12px',
                  zIndex: 10,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    gap: 0.5,
                    alignItems: 'center'
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      width: '6px',
                      height: '6px',
                      borderRadius: '50%',
                      backgroundColor: '#9e9e9e',
                      animation: 'typing 1.4s infinite',
                      display: 'inline-block'
                    }}
                  />
                  <Box
                    component="span"
                    sx={{
                      width: '6px',
                      height: '6px',
                      borderRadius: '50%',
                      backgroundColor: '#9e9e9e',
                      animation: 'typing 1.4s infinite',
                      animationDelay: '0.2s',
                      display: 'inline-block'
                    }}
                  />
                  <Box
                    component="span"
                    sx={{
                      width: '6px',
                      height: '6px',
                      borderRadius: '50%',
                      backgroundColor: '#9e9e9e',
                      animation: 'typing 1.4s infinite',
                      animationDelay: '0.4s',
                      display: 'inline-block'
                    }}
                  />
                </Box>
                <Typography variant="body2" sx={{ color: '#666', fontSize: '0.875rem' }}>
                  Someone in your team is typing
                </Typography>
              </Box>
            )}
            
            <MessageInput
              onSend={handleSendMessage}
              disabled={sending}
              isFunctionCalling={isFunctionCalling}
              functionCallName={functionCallName}
              activeChatId={activeChatId}
              socket={socket}
              isConnected={isConnected}
              currentUserId={user?._id}
              activeChat={activeChat}
              onFileSelect={handleFileSelectMemo}
            />
          </Box>
        )}
        
        {/* Wedding Details Modal */}
        {activeChat && activeChat.wedding?._id && (
          <CoupleInformationModal
            open={showWeddingDetails}
            onClose={() => setShowWeddingDetails(false)}
            chatId={activeChat._id}
          />
        )}
      </Box>
    </Box>
  );
};

export default ChatContainer;
